import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import useResolveManualRide from "../hooks/useResolveManualRide";

// #387 — per-row "attach to real ride" picker for the emergency manual-ride
// review queue. Suggests the target ride in the same class by matching CNO
// (manual ride's competitorNumber == FVD ride's competitorNumber, #386's
// keying scheme), and shows the manual ride's assigned test next to the
// candidate's test so the admin can see a mismatch before trying to attach.
// A mismatch HARD-BLOCKS the Attach button client-side — the server also
// 409s (code "test_mismatch", #388) as the authoritative enforcement, shown
// here as an inline fallback in case of a race or a bypassed client check.
export default function AttachRideDialog({ open, manualRide, onClose, onResolved }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const { resolve, resolving } = useResolveManualRide();

  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [testsById, setTestsById] = useState({});
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const [attachError, setAttachError] = useState(null);

  const classId = manualRide?.classId;

  const loadCandidates = useCallback(async () => {
    if (!classId) {
      setCandidates([]);
      setTestsById({});
      return;
    }
    setCandidatesLoading(true);
    setAttachError(null);
    try {
      const [rides, cls] = await Promise.all([
        scoreApi.rides.listRides(classId).then(unwrap),
        scoreApi.classes.getClass(classId).then(unwrap).catch(() => null),
      ]);
      const real = (rides || []).filter(
        (r) => !r.isManual && (r.id ?? r.rideId) !== (manualRide?.id ?? manualRide?.rideId),
      );
      setCandidates(real);

      if (cls?.showId) {
        const tests = await scoreApi.tests.listTests(cls.showId).then(unwrap).catch(() => []);
        setTestsById(Object.fromEntries((tests || []).map((tst) => [tst.id, tst])));
      } else {
        setTestsById({});
      }
    } catch (err) {
      setCandidates([]);
      setAttachError({ message: err.serverErrorDetail || err.message || t("reviewQueue.attach.loadError") });
    } finally {
      setCandidatesLoading(false);
    }
  }, [classId, manualRide, scoreApi, t]);

  useEffect(() => {
    if (!open) return;
    setSelectedTargetId("");
    setAttachError(null);
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId]);

  const suggested = useMemo(() => {
    if (manualRide?.competitorNumber == null) return null;
    return (
      candidates.find(
        (c) => c.competitorNumber != null && String(c.competitorNumber) === String(manualRide.competitorNumber),
      ) || null
    );
  }, [candidates, manualRide]);

  useEffect(() => {
    if (suggested && !selectedTargetId) {
      setSelectedTargetId(suggested.id ?? suggested.rideId ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);

  const selectedTarget = useMemo(
    () => candidates.find((c) => (c.id ?? c.rideId) === selectedTargetId) || null,
    [candidates, selectedTargetId],
  );

  const manualTest = manualRide?.testId ? testsById[manualRide.testId] : null;
  const targetTest = selectedTarget?.testId ? testsById[selectedTarget.testId] : null;

  const mismatch = !!(
    selectedTarget &&
    manualRide?.testId &&
    selectedTarget.testId &&
    selectedTarget.testId !== manualRide.testId
  );

  const noCandidates = !candidatesLoading && candidates.length === 0;

  const handleAttach = async () => {
    if (!manualRide || !selectedTargetId || mismatch) return;
    setAttachError(null);
    try {
      await resolve(manualRide.id ?? manualRide.rideId, selectedTargetId);
      onResolved?.();
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "test_mismatch") {
        setAttachError({ message: t("reviewQueue.attach.serverMismatch") });
      } else {
        setAttachError({ message: err?.serverErrorDetail || err?.message || t("reviewQueue.attach.serverError") });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("reviewQueue.attach.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.attach.manualRideLabel")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" label={`CNO ${manualRide?.competitorNumber ?? "-"}`} />
              <Typography>{manualRide?.horseName || manualRide?.riderName || "-"}</Typography>
            </Stack>
          </Box>

          {attachError && <Alert severity="error">{attachError.message}</Alert>}

          {candidatesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : noCandidates ? (
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={loadCandidates}>
                  {t("reviewQueue.attach.refresh")}
                </Button>
              }
            >
              {t("reviewQueue.attach.notSynced")}
            </Alert>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id="attach-target-ride-label">
                  {t("reviewQueue.attach.targetRide")}
                </InputLabel>
                <Select
                  labelId="attach-target-ride-label"
                  label={t("reviewQueue.attach.targetRide")}
                  value={selectedTargetId}
                  onChange={(e) => setSelectedTargetId(e.target.value)}
                >
                  {candidates.map((c) => {
                    const id = c.id ?? c.rideId;
                    const isSuggested = suggested && (suggested.id ?? suggested.rideId) === id;
                    // #865 — a candidate ride with no rider/horse name is a
                    // data problem to surface quietly, not the raw ride id;
                    // the id — useful for support telling two nameless
                    // candidates apart — goes in a tooltip instead. MenuItem
                    // must stay Select's direct child, so the Tooltip wraps
                    // only the label inside it.
                    const nameMissing = !c.riderName && !c.horseName;
                    const label = [
                      c.competitorNumber != null ? `#${c.competitorNumber}` : null,
                      c.riderName || c.horseName || t("common.unknownUser"),
                    ]
                      .filter(Boolean)
                      .join(" — ");
                    return (
                      <MenuItem key={id} value={id}>
                        <Tooltip title={nameMissing ? id : ""}>
                          <span>
                            {label}
                            {isSuggested ? ` (${t("reviewQueue.attach.suggested")})` : ""}
                          </span>
                        </Tooltip>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.attach.manualTest")}
                  </Typography>
                  {/* #865 — a testCode lookup miss is a data problem to surface
                      quietly (the existing "no test" copy), not the raw
                      testId; the id — useful for support diagnosing a stale
                      catalog reference — goes in a tooltip instead. */}
                  <Tooltip title={!manualTest && manualRide?.testId ? manualRide.testId : ""}>
                    {/* #1878 — prefer the catalog's own clean name over FVD's raw testCode. */}
                    <Typography>
                      {manualTest?.name || manualTest?.testCode || t("reviewQueue.attach.noTest")}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.attach.targetTest")}
                  </Typography>
                  <Tooltip
                    title={
                      selectedTarget && !targetTest && selectedTarget.testId ? selectedTarget.testId : ""
                    }
                  >
                    {/* #1878 — prefer the catalog's own clean name over FVD's raw testCode. */}
                    <Typography>
                      {selectedTarget
                        ? targetTest?.name || targetTest?.testCode || t("reviewQueue.attach.noTest")
                        : "-"}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>

              {mismatch && (
                <Alert severity="warning">{t("reviewQueue.attach.mismatchBlocked")}</Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("reviewQueue.attach.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!selectedTargetId || mismatch || resolving || noCandidates}
          onClick={handleAttach}
        >
          {resolving ? <CircularProgress size={20} /> : t("reviewQueue.attach.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
